import { gql } from '@apollo/client';
import {
  EditCommand,
  FloorplanUpload,
  ICalibration,
  IFloorplan,
  ISpace,
  ISpaceShape,
  SuggestedCalibration,
} from 'spw-redux/modules/floorplan/types';
import { pick } from 'ramda';
import { graphqlClient } from './utils';
import { SPACE_FIELDS } from './esa_fragments';

export const fetchFloorplan = (floorId: number) =>
  graphqlClient.query<{
    floorplan: Pick<IFloorplan, 'id' | 'floorId' | 's3Path'>;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchFloorplan {
          floorplan(floorId: ${floorId}) {
              id
              floorId
              s3Path
          }
      }`,
  });

export const createFloorplan = (floorId: number, image: File) =>
  graphqlClient.mutate<{
    floorplan: Pick<IFloorplan, 'id' | 'floorId' | 's3Path'>;
  }>({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation CreateFloorplan($floorplan: FloorplanArgs!) {
        floorplan: createFloorplan(floorplan: $floorplan) {
          id
          floorId
          s3Path
        }
      }
    `,
    variables: {
      floorplan: {
        image,
        floorId,
      },
    },
  });

export const archiveFloorplan = (
  floorId: number,
  originalImage: File,
  editHistory: EditCommand[],
  originalDimensions: { width: number; height: number }
) =>
  graphqlClient.mutate<{ floorplanUpload: FloorplanUpload }>({
    mutation: gql`
      mutation ArchiveFloorplan($floorplanUpload: FloorplanUploadArgs!) {
        floorplanUpload: archiveFloorplan(floorplanUpload: $floorplanUpload) {
          id
          status
        }
      }
    `,
    variables: {
      floorplanUpload: {
        floorId,
        originalImage,
        editHistory,
        originalWidth: originalDimensions.width,
        originalHeight: originalDimensions.height,
      },
    },
  });

export const fetchFloorplanUpload = (floorId: number) =>
  graphqlClient.query<{
    floorplanUpload: FloorplanUpload;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchFloorplanUpload($floorId: ID!) {
        floorplanUpload(floorId: $floorId) {
          id
          status
          geojsonStatus
          geojsonS3Path
          geojsonSourceType
        }
      }
    `,
    variables: {
      floorId,
    },
  });

export const downloadFloorplanGeoJson = (floorId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query getFloorplanGeoJson($floorId: ID!) {
        getFloorplanGeojson(floorId: $floorId)
      }
    `,
    variables: {
      floorId,
    },
  });

export const downloadFloorplanPDF = (
  floorId: number,
  filters?: {
    categoriesWithVisibleAltWorkpoints?: string[];
    showWorkstations?: boolean;
    showSpaceUsf?: boolean;
    showFloorUsf?: boolean;
    categories?: string[];
    seatCount?: boolean;
    spaceTypes?: boolean;
    photos?: boolean;
    notes?: boolean;
  }
) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query DownloadFloorplanPDF($floorId: ID!, $filters: FloorplanFilters) {
        pdf: downloadFloorplanPdf(floorId: $floorId, filters: $filters)
      }
    `,
    variables: {
      floorId,
      filters,
    },
  });

export const createSpaces = (params: {
  spaces: ISpace[];
  sourceFloorId: number;
}) => {
  return graphqlClient.mutate<{ createSpaces: ISpace[] }>({
    fetchPolicy: 'no-cache',
    mutation: gql`
      ${SPACE_FIELDS}
      mutation CreateSpaces($spaces: [CreateSpaceArgs], $sourceFloorId: Int!) {
        createSpaces(spaces: $spaces, sourceFloorId: $sourceFloorId) {
          ...SpaceFields
        }
      }
    `,
    variables: {
      spaces: params.spaces,
      sourceFloorId: params.sourceFloorId,
    },
  });
};

export const deleteSpaces = (ids: number[]) =>
  graphqlClient.mutate<{ deletedSpaces: ISpace[] }>({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation DeleteSpaces($ids: [Int]) {
        deletedSpaces: deleteSpaces(ids: $ids) {
          id
        }
      }
    `,
    variables: { ids },
  });

export const parseSpaceToAPISpace = (space: ISpace) =>
  ({
    id: space.id,
    roomNumber: space.roomNumber || '',
    roomName: space.roomName || '',
    seatCount: space.seatCount,
    usf: space.usf,
    floorId: space.floorId,
    spaceTypeId: space.spaceTypeId || space.spaceType.id,
    projectSpaceTypeId: space.projectSpaceTypeId || space.projectSpaceType?.id,
    floorplanPoint: {
      x: space.floorplanPoint?.x,
      y: space.floorplanPoint?.y,
    },
    isAlternativeWorkpoint: space.isAlternativeWorkpoint,
    alternativeWorkpointSeats: space.alternativeWorkpointSeats,
    allowBlankSeats: space.allowBlankSeats,
    allowBlankUsf: space.allowBlankUsf,
  } as ISpace);

export const updateSpaces = (spaces: ISpace[]) =>
  graphqlClient.mutate<{ updatedSpaces: ISpace[] }>({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation UpdateSpaces($spaces: [UpdateSpaceArgs]) {
        updatedSpaces: updateSpaces(spaces: $spaces) {
          id
        }
      }
    `,
    variables: { spaces },
  });

export const calibrate = (floorplan: IFloorplan, calibration: ICalibration) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation Calibrate(
        $floorplanId: Int!
        $constant: Float
        $x1: Float
        $y1: Float
        $x2: Float
        $y2: Float
        $suggestionAcceptedByUser: Boolean
      ) {
        calibrate(
          floorplanId: $floorplanId
          constant: $constant
          x1: $x1
          y1: $y1
          x2: $x2
          y2: $y2
          suggestionAcceptedByUser: $suggestionAcceptedByUser
        ) {
          floorplan_id
          constant
          x1
          y1
          x2
          y2
        }
      }
    `,
    variables: {
      floorplanId: floorplan.id,
      constant: calibration.constant,
      x1: calibration.x1,
      y1: calibration.y1,
      x2: calibration.x2,
      y2: calibration.y2,
      suggestionAcceptedByUser: calibration.suggestionAcceptedByUser,
    },
  });

export const updateSuggestedCalibrationApi = (
  floorplan: IFloorplan,
  suggestedCalibration: SuggestedCalibration
) => {
  return graphqlClient.mutate<{ suggestedCalibration: SuggestedCalibration }>({
    mutation: gql`
      mutation UpdateSuggestedCalibration(
        $floorplanId: ID!
        $suggestedCalibration: SuggestedCalibrationArgs!
      ) {
        suggestedCalibration: updateSuggestedCalibration(
          floorplanId: $floorplanId
          suggestedCalibration: $suggestedCalibration
        ) {
          floorplanId
          constant
          isSnapped
          x1
          y1
          x2
          y2
        }
      }
    `,
    variables: {
      floorplanId: floorplan.id,
      suggestedCalibration: {
        ...pick(
          ['x1', 'y1', 'x2', 'y2', 'constant', 'isSnapped'],
          suggestedCalibration
        ),
      },
    },
  });
};

export const copyFloorShape = (
  inputPolygons: { x: number; y: number }[][][],
  calibration: ICalibration,
  sourceFloorplanId: number,
  targetFloorplanId: number
) => {
  const variables = {
    inputPolygons,
    calibration,
    sourceFloorplanId,
    targetFloorplanId,
  };

  return graphqlClient.mutate({
    mutation: gql`
      mutation CopyFloorShape(
        $inputPolygons: [[[InputPoint]]]!
        $calibration: InputCopiedCalibration
        $sourceFloorplanId: Int!
        $targetFloorplanId: Int!
      ) {
        copyFloorShape(
          polygons: $inputPolygons
          calibration: $calibration
          sourceFloorplanId: $sourceFloorplanId
          targetFloorplanId: $targetFloorplanId
        ) {
          floorplanId
          id
          isFloorUsf
          polygons {
            x
            y
          }
          spaceId
        }
      }
    `,
    variables,
  });
};

export const bulkUpdateShapes = (bulkUpdateShapesInput: {
  floorplanId: number;
  floorId: number;
  shapes: ISpaceShape[];
}) => {
  return graphqlClient.mutate({
    mutation: gql`
      mutation BulkUpdateShapes(
        $floorId: Int!
        $floorplanId: Int!
        $shapes: [InputShape]
      ) {
        bulkUpdateShapes(
          floorId: $floorId
          floorplanId: $floorplanId
          shapes: $shapes
        ) {
          id
          floorplan_id
          space_id
          is_floor_usf
          polygons {
            x
            y
          }
        }
      }
    `,
    variables: bulkUpdateShapesInput,
  });
};

export const fetchSuggestedCalibrationApi = (floorplanId: number) =>
  graphqlClient.query<{ suggestedCalibration: SuggestedCalibration }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetSuggestedCalibration($floorplanId: Int!) {
        suggestedCalibration: floorplanSuggestedCalibration(
          floorplanId: $floorplanId
        ) {
          floorplanId
          x1
          x2
          y1
          y2
          isSnapped
          constant
          status
        }
      }
    `,
    variables: {
      floorplanId,
    },
  });
